'use client';

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MessageCircle, Heart, Eye, Activity, RefreshCw } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


/**
 * LiveActivityBadge Component
 * 
 * Displays real-time activity indicators with animated pulses
 * Used across Community Hub, Prayer Wall, and Live Streams
 * 
 * Features:
 * - Animated notification pulses
 * - Real-time counters
 * - Activity indicators
 * - Refresh notifications
 */

const LiveActivityBadge = ({
  type = 'viewers',
  count = 0,
  showPulse = true,
  showNewIndicator = false,
  newCount = 0,
  size = 'md',
  variant = 'default',
  label = '',
  onRefresh = null,
  className = ''
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayCount, setDisplayCount] = useState(count);
  const [hasNewActivity, setHasNewActivity] = useState(showNewIndicator);

  // Animate count changes
  useEffect(() => {
    if (count !== displayCount) {
      setIsAnimating(true);
      setDisplayCount(count);
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [count, displayCount]);

  // Handle new activity indicator
  useEffect(() => {
    if (showNewIndicator && newCount > 0) {
      setHasNewActivity(true);
      
      // Auto-dismiss after 3 seconds
      const timer = setTimeout(() => {
        setHasNewActivity(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNewIndicator, newCount]);

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'viewers':
        return Eye;
      case 'users':
        return Users;
      case 'comments':
        return MessageCircle;
      case 'prayers':
        return Heart;
      case 'activity':
        return Activity;
      default:
        return Users;
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          badge: 'px-2 py-1 text-xs',
          icon: 'w-3 h-3',
          pulse: 'w-2 h-2',
          notification: 'w-3 h-3 text-[8px]'
        };
      case 'lg':
        return {
          badge: 'px-4 py-2 text-base',
          icon: 'w-5 h-5',
          pulse: 'w-3 h-3',
          notification: 'w-5 h-5 text-xs'
        };
      default:
        return {
          badge: 'px-3 py-1.5 text-sm',
          icon: 'w-4 h-4',
          pulse: 'w-2.5 h-2.5',
          notification: 'w-4 h-4 text-[10px]'
        };
    }
  };

  // Get variant classes
  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'danger':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'live':
        return 'bg-red-600 text-white border-red-700';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const Icon = getIcon();
  const sizeClasses = getSizeClasses();
  const variantClasses = getVariantClasses();

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <motion.div
        initial={false}
        animate={isAnimating ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 0.3 }}
        className={`
          flex items-center gap-2 rounded-full border font-medium
          ${sizeClasses?.badge}
          ${variantClasses}
          ${onRefresh ? 'cursor-pointer hover:opacity-80' : ''}
        `}
        onClick={onRefresh}
      >
        {/* Icon with optional pulse */}
        <div className="relative">
          <Icon className={sizeClasses?.icon} />
          {showPulse && (
            <>
              <span className="absolute inset-0 animate-ping">
                <Icon className={sizeClasses?.icon} />
              </span>
              {variant === 'live' && (
                <span 
                  className={`absolute -top-0.5 -right-0.5 ${sizeClasses?.pulse} bg-white rounded-full animate-pulse`}
                ></span>
              )}
            </>
          )}
        </div>

        {/* Count */}
        <motion.span
          key={displayCount}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="font-semibold"
        >
          {displayCount?.toLocaleString()}
        </motion.span>

        {/* Label */}
        {label && <span className="hidden sm:inline">{label}</span>}

        {/* Refresh icon */}
        {onRefresh && (
          <RefreshCw className={`${sizeClasses?.icon} ml-1`} />
        )}
      </motion.div>
      {/* New activity notification badge */}
      <AnimatePresence>
        {hasNewActivity && newCount > 0 && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className={`
              absolute -top-1 -right-1 
              bg-red-600 text-white rounded-full 
              flex items-center justify-center
              font-bold shadow-lg
              ${sizeClasses?.notification}
            `}
          >
            {newCount > 99 ? '99+' : newCount}
          </motion.div>
        )}
      </AnimatePresence>
      {/* Pulse ring animation for new activity */}
      {hasNewActivity && (
        <span className="absolute inset-0 rounded-full animate-ping bg-red-600 opacity-20"></span>
      )}
    </div>
  );
};

LiveActivityBadge.propTypes = {
  type: PropTypes?.oneOf(['viewers', 'users', 'comments', 'prayers', 'activity']),
  count: PropTypes?.number,
  showPulse: PropTypes?.bool,
  showNewIndicator: PropTypes?.bool,
  newCount: PropTypes?.number,
  size: PropTypes?.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes?.oneOf(['default', 'success', 'warning', 'danger', 'info', 'live']),
  label: PropTypes?.string,
  onRefresh: PropTypes?.func,
  className: PropTypes?.string
};

export default LiveActivityBadge;